import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  CurrentReleaseNote,
  ReleaseNote,
  ReleaseNoteStatus,
} from 'app/models/release-note.model';
import { SiteReviewService } from './site-review.service';

/**
 * Feature flag code gating the whole subsystem (popup, "What's New" menu item,
 * and the admin screens). Must exist as a row in admin/feature-flags and be
 * enabled per environment — an unregistered code resolves to off.
 */
export const RELEASE_NOTES_FEATURE_CODE = 'REL_NOTES';

/**
 * Release Notes API client. See docs/release-notes-api.md for the contract.
 *
 * Session is a `sid` query param, not a bearer token — apiServer and sessionId
 * come from SiteReviewService rather than the environment/localStorage directly.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseNotesService {
  constructor(
    private http: HttpClient,
    private siteReview: SiteReviewService,
  ) {}

  private get base(): string {
    return `${this.siteReview.apiServer}/api/release-notes`;
  }

  private withSid(extra?: Record<string, string | number | boolean>): HttpParams {
    let params = new HttpParams().set('sid', this.siteReview.sessionId);
    if (extra) {
      for (const k of Object.keys(extra)) {
        const v = extra[k];
        if (v !== undefined && v !== null && v !== '') {
          params = params.set(k, String(v));
        }
      }
    }
    return params;
  }

  // ---------------------------------------------------------------- end user

  /**
   * The release note the signed-in user still needs to see, or null.
   * The server is authoritative: it returns null once the user has ticked
   * "don't show this again" for the currently published version.
   */
  getCurrent(): Observable<CurrentReleaseNote | null> {
    return this.http.get<CurrentReleaseNote | null>(`${this.base}/current`, {
      params: this.withSid(),
    });
  }

  /**
   * Records that the popup was closed. `dontShowAgain` false means "remind me
   * at next sign-in"; true suppresses it until a newer version is published.
   */
  dismiss(releaseNoteID: number, dontShowAgain: boolean): Observable<any> {
    return this.http.post(
      `${this.base}/${releaseNoteID}/dismiss`,
      { dontShowAgain },
      { params: this.withSid() },
    );
  }

  /** Latest published note regardless of dismissal — powers the "What's New" menu item. */
  getLatest(): Observable<ReleaseNote | null> {
    return this.http.get<ReleaseNote | null>(`${this.base}/latest`, {
      params: this.withSid(),
    });
  }

  // ------------------------------------------------------------------ admin

  list(status?: ReleaseNoteStatus): Observable<ReleaseNote[]> {
    return this.http.get<ReleaseNote[]>(this.base, { params: this.withSid({ status }) });
  }

  getById(releaseNoteID: number): Observable<ReleaseNote> {
    return this.http.get<ReleaseNote>(`${this.base}/${releaseNoteID}`, {
      params: this.withSid(),
    });
  }

  add(payload: ReleaseNote): Observable<ReleaseNote> {
    return this.http.post<ReleaseNote>(this.base, payload, { params: this.withSid() });
  }

  save(releaseNoteID: number, payload: ReleaseNote): Observable<ReleaseNote> {
    return this.http.put<ReleaseNote>(`${this.base}/${releaseNoteID}`, payload, {
      params: this.withSid(),
    });
  }

  delete(releaseNoteID: number): Observable<any> {
    return this.http.delete(`${this.base}/${releaseNoteID}`, { params: this.withSid() });
  }

  /**
   * Publishes a version and clears every user's dismissal for it — this is the
   * "flag reset" that makes the popup reappear for everyone on their next sign-in.
   */
  publish(releaseNoteID: number): Observable<ReleaseNote> {
    return this.http.post<ReleaseNote>(
      `${this.base}/${releaseNoteID}/publish`,
      {},
      { params: this.withSid() },
    );
  }
}
